import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import Decimal from 'decimal.js'
import IconColorMeter from '@/components/icons/IconColorMeter'
import {
  accountInfoState,
  showAdjustLeveragePopupState
} from '@/recoil/AccountInfo'
import { positionDetailValue, collateralInfoValue } from '@/recoil/Position'
import numberFormat from '@/utils/numberFormat'

import './MarginInfo.css'

function reScale({ marginRatioPercentage, max, min, maxAngle }) {
  const scale = Decimal(max).sub(min).toNumber()
  const adjustValue = marginRatioPercentage.sub(min).abs()
  const percentage = adjustValue.div(scale).toNumber()
  return Decimal(maxAngle).sub(Decimal(60).mul(percentage)).toNumber()
}

function getRiskMeterAngle(marginRatioPercentage, maintenanceMargin) {
  if (marginRatioPercentage.lte(10)) {
    return reScale({
      marginRatioPercentage,
      max: 10,
      min: maintenanceMargin,
      maxAngle: 180
    })
  }

  if (marginRatioPercentage.gte(100)) {
    return reScale({
      marginRatioPercentage,
      max: 1000,
      min: 100,
      maxAngle: 60
    })
  }

  return reScale({
    marginRatioPercentage,
    max: 100,
    min: 10,
    maxAngle: 120
  })
}

export default function MarginInfo() {
  const [accountInfo] = useRecoilState(accountInfoState)
  const setAdjustLeveragePopup = useSetRecoilState(showAdjustLeveragePopupState)
  const { totalMaintenanceMargin, totalPositionNotionalValue } =
    useRecoilValue(positionDetailValue)
  const { totalCollateral, freeCollateral } =
    useRecoilValue(collateralInfoValue)

  const accountId = accountInfo.account_id

  const marginRatio = Decimal.min(
    10,
    Decimal(totalCollateral).div(totalPositionNotionalValue)
  )
  const maintenanceMargin = Decimal(totalPositionNotionalValue).eq(0)
    ? Decimal(0.03)
    : Decimal(totalMaintenanceMargin).div(totalPositionNotionalValue)

  // const currentLeverage = Decimal(1).div(marginRatio).toFixed(2, Decimal.ROUND_CEIL)

  // console.log(currentLeverage)

  const marginRatioPercentage = marginRatio.mul(100)
  const riskLevel = marginRatioPercentage.lte(10)
    ? 'high'
    : marginRatioPercentage.lt(100)
    ? 'medium'
    : 'low'

  const riskIndicatorStyle = {
    transform: `rotate(${getRiskMeterAngle(
      marginRatioPercentage,
      maintenanceMargin.mul(100).toNumber()
    )}deg)`
  }

  return (
    <div className="margin-info">
      <div
        className="leverage-block"
        onClick={() => accountId && setAdjustLeveragePopup(true)}
      >
        Leverage:
        <span className="leverage-value">
          {accountId ? `${accountInfo.max_leverage}x` : '-'}
        </span>
      </div>
      <div className="collateral-block">
        <div className="collateral-title">Collateral</div>
        <span className="value-highlight">
          {numberFormat(freeCollateral, 'max0')}
        </span>
        {' / '}
        {numberFormat(totalCollateral, 'max0')} USDC
      </div>
      <div className="tooltip-container margin-ratio-block">
        <div className="risk-level-block">
          <div className="margin-ratio-title">Margin Ratio</div>
          <div className={`risk-level ${riskLevel}`}>
            {accountId
              ? marginRatioPercentage.toFixed(2, Decimal.ROUND_DOWN)
              : '-'}
            {'% '}
            <span className="risk-level-text">{riskLevel} Risk</span>
          </div>
        </div>
        <div className="risk-meter">
          <IconColorMeter />
          <div className="risk-indicator" style={riskIndicatorStyle} />
        </div>
        <div className="tooltip">
          Futures Margin Ratio represents your amount of collateral posted
          relative to the value of your positions outstanding. Futures margin
          ratio = Total collateral value / Total position notional value
        </div>
      </div>
    </div>
  )
}
